import {
  type DatabaseAdapter,
  type ResolvedSelection,
  type SelectionsApi,
} from '@databases/selections-api';
import { DatabaseFieldType, type DatabaseField } from '@databases/types/database';

import { DEPENDENCY_FIELD_TYPES } from '../../constants';

type ActiveSelection = Exclude<ResolvedSelection, { mode: 'idle' }>;

/**
 * Builds all data needed for the action bar from selections and database state.
 * Combines selection-derived data with business rules.
 */
export function buildActionBarSelectionsData(
  api: SelectionsApi,
  selection: ActiveSelection,
  databaseAdapter: DatabaseAdapter
) {
  const count = getSelectionCount(selection);
  const isDatabaseLocked = databaseAdapter.isLocked();

  return {
    canMoveUp: api.canMoveUp(),
    canMoveDown: api.canMoveDown(),
    canMoveLeft: api.canMoveLeft(),
    canMoveRight: api.canMoveRight(),
    count,
    fieldsWithSelectionsCount:
      selection.mode === 'cell'
        ? new Set(selection.values.map((v) => v.fieldId)).size
        : 0,
    selectionMode: selection.mode,
    selectedEntriesCount: selection.mode === 'row' ? selection.entries.length : 0,
    selectedValues: selection.mode === 'cell' ? selection.values : [],

    // Business rules
    canEditContent: !isDatabaseLocked,
    isDatabaseLocked,
    isDeleteColumnsDisabled: computeIsDeleteColumnsDisabled(selection, databaseAdapter),
    isDependencyFieldSelected:
      selection.mode === 'column'
        ? selection.fields.some((f) => DEPENDENCY_FIELD_TYPES.has(f.field.type))
        : false,
    shouldShowClearCells: computeShouldShowClearCells(selection),
  };
}

function getSelectionCount(selection: ActiveSelection) {
  if (selection.mode === 'cell') {
    return selection.values.length;
  }

  if (selection.mode === 'column') {
    return selection.fields.length;
  }

  return selection.entries.length;
}

/**
 * Computes whether delete columns should be disabled.
 * Matches parity with legacy selector: selectIsDeleteColumnsDisabled
 *
 * Returns true if:
 * - In column mode AND (all fields selected OR only 1 field exists)
 * - In cell mode AND all fields have selections
 * - In row mode AND all fields have selections
 * - OR only 1 field exists (regardless of mode)
 */
function computeIsDeleteColumnsDisabled(
  selection: ActiveSelection,
  databaseAdapter: DatabaseAdapter
): boolean {
  const allFieldsCount = databaseAdapter.getAllFieldIds().length;

  if (allFieldsCount === 1) {
    return true;
  }

  if (selection.mode === 'column') {
    return allFieldsCount === selection.fields.length;
  }

  if (selection.mode === 'cell') {
    const uniqueFieldIds = new Set(selection.values.map((v) => v.fieldId));

    return allFieldsCount === uniqueFieldIds.size;
  }

  return false;
}

function getSelectedFields(selection: ActiveSelection): DatabaseField[] {
  if (selection.mode === 'column') {
    return selection.fields.map((f) => f.field);
  }

  if (selection.mode === 'cell') {
    const fieldMap = new Map<string, DatabaseField>();

    for (const value of selection.values) {
      if (!fieldMap.has(value.fieldId)) {
        fieldMap.set(value.fieldId, value.field);
      }
    }

    return Array.from(fieldMap.values());
  }

  return [];
}

/**
 * Computes whether the clear cells action should be shown.
 * Matches parity with legacy hook: useSharedActionsMethods.shouldShowClearCells
 *
 * Returns:
 * - field.settings.allowEdit if PAGE_STATUS or PAGE_LABELS field is selected
 * - false if any dependency field type is selected
 * - true otherwise
 */
function computeShouldShowClearCells(selection: ActiveSelection): boolean {
  const fields = getSelectedFields(selection);

  for (const field of fields) {
    if (
      field.type === DatabaseFieldType.PAGE_STATUS ||
      field.type === DatabaseFieldType.PAGE_LABELS
    ) {
      return field.settings.allowEdit ?? false;
    }

    if (DEPENDENCY_FIELD_TYPES.has(field.type)) {
      return false;
    }
  }

  return true;
}
